import { useState } from "react";
import EntityManager from "@/components/EntityManager";
import MergeEntitiesDialog from "@/components/MergeSurgeonsDialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { GitMerge, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

export default function SurgeonsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "admin_master";
  const [merging, setMerging] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [manualMergeOpen, setManualMergeOpen] = useState(false);

  async function autoMergeByCRM() {
    if (!isAdmin || merging) return;
    if (!(await confirm({ description: "Auto-mesclar cirurgiões duplicados por CRM?\n\n" +
      "O sistema vai juntar todos os cadastros que compartilham o mesmo CRM, " +
      "manter o que tiver mais casos como canônico e transformar os outros nomes " +
      "em apelidos (assim os próximos mapas reconhecem automaticamente).\n\n" +
      "Casos vinculados (principal e auxiliar) serão atualizados pra apontar pro " +
      "cadastro canônico. Produção, Faturamento e Financeiro refletem na hora.\n\n" +
      "Esta ação não pode ser desfeita facilmente. Continuar?" }))) return;
    setMerging(true);
    try {
      const r = await api.post("/surgeons/auto-merge-by-crm", {});
      const d = r.data || {};
      if (d.merges_done === 0) {
        toast.success("Nenhum CRM duplicado encontrado.");
      } else {
        const totalMerged = (d.details || []).reduce((acc, x) => acc + (x.stats?.merged || 0), 0);
        const totalCases = (d.details || []).reduce(
          (acc, x) => acc + (x.stats?.cases_principal_updated || 0) + (x.stats?.cases_assistant_updated || 0),
          0
        );
        toast.success(
          `${d.merges_done} grupo(s) de CRM mesclado(s). ` +
          `${totalMerged} cadastros duplicados removidos. ${totalCases} casos atualizados.`,
          { duration: 7000 }
        );
        setReloadKey((k) => k + 1);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao auto-mesclar");
    } finally {
      setMerging(false);
    }
  }

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setManualMergeOpen(true)}
            data-testid="btn-manual-merge"
            className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
            title="Mesclar cirurgiões manualmente (ex: nomes diferentes mesma pessoa, sem CRM ou CRM divergente)"
          >
            <GitMerge className="h-4 w-4 mr-2" /> Mesclar manualmente
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={autoMergeByCRM}
            disabled={merging}
            data-testid="btn-auto-merge-crm"
            className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
            title="Junta cirurgiões duplicados que compartilham o mesmo CRM em um só cadastro canônico"
          >
            {merging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitMerge className="h-4 w-4 mr-2" />}
            Auto-mesclar duplicados por CRM
          </Button>
        </div>
      )}
      <MergeEntitiesDialog
        open={manualMergeOpen}
        onClose={() => setManualMergeOpen(false)}
        endpoint="/surgeons"
        entityLabel="cirurgião"
        onMerged={() => setReloadKey((k) => k + 1)}
      />
      <EntityManager
        key={reloadKey}
        testidPrefix="surgeons"
        title="Cirurgiões"
        description="Equipe de cirurgiões cadastrados. Usados na filtragem do mapa bruto."
        endpoint="/surgeons"
        columns={[
          { key: "full_name", label: "Nome" },
          { key: "crm", label: "CRM" },
          { key: "specialty", label: "Especialidade" },
          { key: "is_team_member", label: "Equipe?", render: (s) => s.is_team_member ? "Sim" : "Não" },
          { key: "active", label: "Ativo?", render: (s) => s.active ? "Sim" : "Não" },
        ]}
        fields={[
          { name: "full_name", label: "Nome completo", required: true, fullWidth: true },
          { name: "display_name", label: "Nome de exibição" },
          { name: "crm", label: "CRM" },
          { name: "specialty", label: "Especialidade" },
          { name: "is_team_member", label: "Pertence à equipe", type: "checkbox", default: true, checkboxLabel: "Faz parte da equipe (entra no filtro do mapa)" },
          { name: "active", label: "Ativo", type: "checkbox", default: true, checkboxLabel: "Ativo" },
          { name: "notes", label: "Observações", type: "textarea", fullWidth: true },
        ]}
      />
    </div>
  );
}
